import { useMemo } from "react";
import { useGameStore } from "@/store/gameStore";
import { useLegalActions } from "@/hooks/useLegalActions";
import type { EngineAction, EquipSlot } from "@/engine/GameEngine";
import type { Suit, DeckCard } from "@/types/cards";
import Card from "./Card";

type PlayCardAction = Extract<EngineAction, { type: "play-card" }>;

const SLOT_LABEL: Record<EquipSlot, string> = {
  weapon: "武器",
  armor: "防具",
  accessory: "饰品",
};
const SLOT_ORDER: EquipSlot[] = ["weapon", "armor", "accessory"];
// 杀戮之心可选花色（红色/黑色/蓝色，恒显三色盲选）
const SUIT_ORDER: NonNullable<Suit>[] = ["红", "黑", "蓝"];
const SUIT_COLOR: Record<NonNullable<Suit>, string> = {
  红: "#e57373",
  黑: "#cfd8dc",
  蓝: "#64b5f6",
};

// Hook：暴露当前目标选择态与目标 id 集合 + 选择目标的回调
// Board 据此给 SeatPanel/PlayerPanel 传 isTargetable + onTarget
export function useTargetPicker(): {
  isActive: boolean;
  targetIds: string[];
  pickTarget: (targetId: string) => void;
  cancel: () => void;
  hint: string;
  // 缴械/掠夺：装备槽二步选择
  slotTargetId: string | null;
  pickSlot: (slot: EquipSlot) => void;
  // 杀戮之心：花色二步选择
  suitTargetId: string | null;
  pickSuit: (suit: NonNullable<Suit>) => void;
  // 飞龙探云手：背面盲选手牌二步选择
  stealTargetId: string | null;
  stealCards: DeckCard[];
  pickStealCard: (uid: number) => void;
  backToTargets: () => void;
} {
  const selectedCardUid = useGameStore((s) => s.selectedCardUid);
  const targetMode = useGameStore((s) => s.targetMode);
  const pendingSkillName = useGameStore((s) => s.pendingSkillName);
  const pendingPairFirst = useGameStore((s) => s.pendingPairFirst);
  const pendingSlotTargetId = useGameStore((s) => s.pendingSlotTargetId);
  const pendingSuitTargetId = useGameStore((s) => s.pendingSuitTargetId);
  const pendingStealTargetId = useGameStore((s) => s.pendingStealTargetId);
  const state = useGameStore((s) => s.state);
  const dispatch = useGameStore((s) => s.dispatch);
  const setSelectedCard = useGameStore((s) => s.setSelectedCard);
  const setTargetMode = useGameStore((s) => s.setTargetMode);
  const setPendingSkillName = useGameStore((s) => s.setPendingSkillName);
  const setPendingPairFirst = useGameStore((s) => s.setPendingPairFirst);
  const setPendingSlotTargetId = useGameStore((s) => s.setPendingSlotTargetId);
  const setPendingSuitTargetId = useGameStore((s) => s.setPendingSuitTargetId);
  const setPendingStealTargetId = useGameStore((s) => s.setPendingStealTargetId);
  const substitutePicks = useGameStore((s) => s.substitutePicks);
  const clearSubstitutePicks = useGameStore((s) => s.clearSubstitutePicks);
  const legalActions = useLegalActions();

  // 替换出牌：用 substitutePicks 填入动作的 substituteCardUids
  function dispatchSubstitute(action: PlayCardAction) {
    if (action.substituteCardUids && action.substituteCardUids[0] === -1 && substitutePicks.length === 2) {
      dispatch({ ...action, substituteCardUids: [substitutePicks[0], substitutePicks[1]] });
    } else {
      dispatch(action);
    }
    clearSubstitutePicks();
  }

  const isActive = selectedCardUid != null && targetMode !== "none";

  // 从合法动作中筛选匹配 selectedCardUid (+ pendingSkillName) 的所有 play-card
  const matchedActions = useMemo<PlayCardAction[]>(() => {
    if (selectedCardUid == null) return [];
    return legalActions.filter((a): a is PlayCardAction => {
      if (a.type !== "play-card") return false;
      if (a.cardUid !== selectedCardUid) return false;
      if (pendingSkillName && a.skillName !== pendingSkillName) return false;
      return true;
    });
  }, [legalActions, selectedCardUid, pendingSkillName]);

  // 命运链接/呼神项链两步选择
  const isPairMode = targetMode === "pair";
  // 呼神项链：双目标可选——存在不带 targetId2 的单体动作
  const pairAllowsSingle = matchedActions.some((a) => !a.targetId2);
  // 缴械/掠夺：先选目标，再选装备槽
  const isSlotMode = targetMode === "slot";
  // 杀戮之心：先选目标，再选花色
  const isSuitMode = targetMode === "suit";
  // 飞龙探云手：先选目标，再背面盲选一张手牌
  const isStealMode = targetMode === "steal-card";
  const secondStepTargetId =
    pendingSlotTargetId ?? pendingSuitTargetId ?? pendingStealTargetId;

  const targetIds = useMemo(() => {
    if (isSlotMode || isSuitMode || isStealMode) {
      // 已选定目标：保持该目标高亮（第二步浮层操作）
      if (secondStepTargetId) return [secondStepTargetId];
      return Array.from(
        new Set(
          matchedActions
            .map((a) => a.targetId)
            .filter((v): v is string => Boolean(v))
        )
      );
    }
    if (!isPairMode) {
      return Array.from(
        new Set(
          matchedActions
            .map((a) => a.targetId)
            .filter((v): v is string => Boolean(v))
        )
      );
    }
    // pair 模式
    if (!pendingPairFirst) {
      // 第一步：所有出现在配对中的玩家（targetId + targetId2）
      const ids = new Set<string>();
      for (const a of matchedActions) {
        if (a.targetId) ids.add(a.targetId);
        if (a.targetId2) ids.add(a.targetId2);
      }
      return Array.from(ids);
    }
    // 第二步：与 pendingPairFirst 配对的玩家
    const ids = new Set<string>();
    for (const a of matchedActions) {
      if (a.targetId === pendingPairFirst && a.targetId2) ids.add(a.targetId2);
      if (a.targetId2 === pendingPairFirst && a.targetId) ids.add(a.targetId);
    }
    // 呼神项链可选单目标：第一目标保持可点（再次点击确认只攻击一个）
    if (
      pairAllowsSingle &&
      matchedActions.some((a) => a.targetId === pendingPairFirst && !a.targetId2)
    ) {
      ids.add(pendingPairFirst);
    }
    return Array.from(ids);
  }, [
    matchedActions,
    isPairMode,
    isSlotMode,
    isSuitMode,
    isStealMode,
    pendingPairFirst,
    pairAllowsSingle,
    secondStepTargetId,
  ]);

  // 飞龙探云手第二步：目标手牌（仅保留仍可抽取的牌，UI 只渲染卡背）
  const stealCards = useMemo<DeckCard[]>(() => {
    if (!isStealMode || !pendingStealTargetId || !state) return [];
    const target = state.players.find((p) => p.id === pendingStealTargetId);
    if (!target) return [];
    const validUids = new Set(
      matchedActions
        .filter((a) => a.targetId === pendingStealTargetId)
        .map((a) => a.stealCardUid)
        .filter((v): v is number => typeof v === "number")
    );
    return target.hand.filter((c) => validUids.has(c.uid));
  }, [isStealMode, pendingStealTargetId, state, matchedActions]);

  const hint = isSlotMode
    ? pendingSlotTargetId
      ? "选择要弃置/夺取的装备"
      : "选择目标玩家"
    : isSuitMode
      ? pendingSuitTargetId
        ? "盲选一种花色：弃去目标该花色的所有手牌"
        : "选择目标玩家"
      : isStealMode
        ? pendingStealTargetId
          ? "从对手背面手牌中选择一张抽取"
          : "选择目标玩家"
        : isPairMode
          ? pendingPairFirst
            ? pairAllowsSingle
              ? "选择第二个目标（再次点击第一个目标则只攻击一个）"
              : "选择第二个链接目标"
            : "选择第一个目标"
          : "选择目标";

  function pickTarget(targetId: string) {
    // 命运链接/呼神项链两步选择
    if (isPairMode) {
      if (!pendingPairFirst) {
        // 第一步：记录第一个目标
        setPendingPairFirst(targetId);
        return;
      }
      // 呼神项链可选单目标：再次点击第一目标 → 只攻击一个
      if (pairAllowsSingle && targetId === pendingPairFirst) {
        const single = matchedActions.find(
          (a) => a.targetId === pendingPairFirst && !a.targetId2
        );
        if (single) {
          dispatchSubstitute(single);
          return;
        }
      }
      // 第二步：找到匹配的配对动作
      const found = matchedActions.find(
        (a) =>
          (a.targetId === pendingPairFirst && a.targetId2 === targetId) ||
          (a.targetId2 === pendingPairFirst && a.targetId === targetId)
      );
      if (found) {
        dispatchSubstitute(found);
      } else {
        cancel();
      }
      return;
    }

    // 缴械/掠夺：先选目标，若多件装备则进入槽位选择
    if (isSlotMode) {
      const forTarget = matchedActions.filter((a) => a.targetId === targetId);
      if (forTarget.length === 0) {
        cancel();
        return;
      }
      if (forTarget.length === 1 && forTarget[0].slot) {
        dispatchSubstitute(forTarget[0]); // 目标只有一件装备：直接结算
        return;
      }
      // 多件装备：进入槽位选择
      setPendingSlotTargetId(targetId);
      return;
    }

    // 杀戮之心：选目标后恒进入花色盲选（不得因目标只含单一花色就跳过弹窗，
    // 否则旁观者可从"未弹窗"推断其手牌构成）
    if (isSuitMode) {
      const forTarget = matchedActions.filter((a) => a.targetId === targetId);
      if (forTarget.length === 0) {
        cancel();
        return;
      }
      setPendingSuitTargetId(targetId);
      return;
    }

    // 飞龙探云手：选目标后进入背面盲选
    if (isStealMode) {
      const forTarget = matchedActions.filter((a) => a.targetId === targetId);
      if (forTarget.length === 0) {
        cancel();
        return;
      }
      if (forTarget.length === 1 && forTarget[0].stealCardUid != null) {
        dispatch(forTarget[0]); // 目标只有一张手牌：直接抽取
        return;
      }
      setPendingStealTargetId(targetId);
      return;
    }

    // 普通单目标选择
    const found = matchedActions.find((a) => a.targetId === targetId);
    if (found) {
      dispatchSubstitute(found);
    } else {
      cancel();
    }
  }

  // 缴械/掠夺：选定目标后点击具体装备槽
  function pickSlot(slot: EquipSlot) {
    if (!pendingSlotTargetId) return;
    const found = matchedActions.find(
      (a) => a.targetId === pendingSlotTargetId && a.slot === slot
    );
    if (found) {
      dispatchSubstitute(found);
    } else {
      cancel();
    }
  }

  // 杀戮之心：选定目标后点击具体花色
  function pickSuit(suit: NonNullable<Suit>) {
    if (!pendingSuitTargetId) return;
    const found = matchedActions.find(
      (a) => a.targetId === pendingSuitTargetId && a.suit === suit
    );
    if (found) {
      dispatchSubstitute(found);
    } else {
      cancel();
    }
  }

  // 飞龙探云手：选定目标后背面盲点一张
  function pickStealCard(uid: number) {
    if (!pendingStealTargetId) return;
    const found = matchedActions.find(
      (a) => a.targetId === pendingStealTargetId && a.stealCardUid === uid
    );
    if (found) {
      dispatch(found);
    } else {
      cancel();
    }
  }

  // 从第二步选择退回目标选择
  function backToTargets() {
    setPendingSlotTargetId(null);
    setPendingSuitTargetId(null);
    setPendingStealTargetId(null);
  }

  function cancel() {
    setSelectedCard(null);
    setTargetMode("none");
    setPendingSkillName(null);
    setPendingPairFirst(null);
    setPendingSlotTargetId(null);
    setPendingSuitTargetId(null);
    setPendingStealTargetId(null);
  }

  return {
    isActive,
    targetIds,
    pickTarget,
    cancel,
    hint,
    slotTargetId: isSlotMode ? pendingSlotTargetId : null,
    pickSlot,
    suitTargetId: isSuitMode ? pendingSuitTargetId : null,
    pickSuit,
    stealTargetId: isStealMode ? pendingStealTargetId : null,
    stealCards,
    pickStealCard,
    backToTargets,
  };
}

// 顶部 banner：提示选择目标 + 取消按钮；第二步显示装备槽/花色/手牌菜单
export default function TargetPicker() {
  const {
    isActive,
    cancel,
    hint,
    slotTargetId,
    pickSlot,
    suitTargetId,
    pickSuit,
    stealTargetId,
    stealCards,
    pickStealCard,
    backToTargets,
  } = useTargetPicker();
  const state = useGameStore((s) => s.state);

  if (!isActive || !state) return null;

  const slotTarget = slotTargetId
    ? state.players.find((p) => p.id === slotTargetId)
    : undefined;

  return (
    <div className="target-picker" role="status">
      <span className="target-picker__hint">{hint}</span>
      {slotTargetId && slotTarget && (
        <div className="target-picker__slots" role="dialog">
          {SLOT_ORDER.filter((slot) => slotTarget.equip[slot]).map((slot) => {
            const eq = slotTarget.equip[slot]!;
            return (
              <button
                key={slot}
                className="target-picker__slot-btn"
                title={eq.effectText ?? ""}
                onClick={() => pickSlot(slot)}
              >
                <span className="target-picker__slot-kind">
                  {SLOT_LABEL[slot]}
                </span>
                <span className="target-picker__slot-name">{eq.name}</span>
              </button>
            );
          })}
          <button
            className="target-picker__slot-back"
            onClick={backToTargets}
          >
            重选目标
          </button>
        </div>
      )}
      {suitTargetId && (
        <div className="target-picker__slots" role="dialog">
          {SUIT_ORDER.map((suit) => (
            <button
              key={suit}
              className="target-picker__slot-btn target-picker__suit-btn"
              title={`弃去目标所有${suit}色手牌`}
              onClick={() => pickSuit(suit)}
            >
              <span
                className="target-picker__slot-kind"
                style={{ color: SUIT_COLOR[suit] }}
              >
                {suit}色
              </span>
            </button>
          ))}
          <button
            className="target-picker__slot-back"
            onClick={backToTargets}
          >
            重选目标
          </button>
        </div>
      )}
      {stealTargetId && (
        <div className="target-picker__steal" role="dialog">
          <div className="target-picker__steal-cards">
            {stealCards.map((c) => (
              <Card
                key={c.uid}
                card={c}
                faceDown
                compact
                highlight
                onClick={() => pickStealCard(c.uid)}
              />
            ))}
          </div>
          <button
            className="target-picker__slot-back"
            onClick={backToTargets}
          >
            重选目标
          </button>
        </div>
      )}
      <button className="target-picker__cancel" onClick={cancel}>
        取消
      </button>
    </div>
  );
}
